import type { VacationRequest } from '@domain/entities/VacationRequest';
import type { RequestStatus } from '@domain/vocabulary';

/**
 * What changed between two deliveries of the same live query.
 *
 * A Firestore subscription hands over the whole result set every time, so
 * "something happened" has to be derived. Deriving it here rather than inside a
 * hook is what makes it testable: the interesting cases — a request that arrived,
 * one that changed status, one that merely had a field touched — are three lines
 * of comparison and a long tail of ways to get them wrong.
 *
 * Departures are reported, and that took a bug to learn. Nothing here deletes an
 * absence, so it looked as though a request could only ever change status under
 * a watcher's nose. But a subscription is *filtered* — a leader watches the
 * statuses that still need deciding — and a request that is cancelled or decided
 * falls out of that filter entirely. Ignoring departures left a cancelled
 * request sitting on the leader's board until somebody reloaded the page.
 */

export interface RequestArrival {
  readonly request: VacationRequest;
}

export interface RequestTransition {
  readonly request: VacationRequest;
  readonly from: RequestStatus;
  readonly to: RequestStatus;
}

export interface RequestChanges {
  readonly arrived: readonly RequestArrival[];
  readonly moved: readonly RequestTransition[];
  /**
   * No longer matched by the query — cancelled, or decided out of the queue.
   * Worth refreshing for, rarely worth announcing: the watcher is usually the
   * one who just caused it.
   */
  readonly left: readonly VacationRequest[];
}

export const EMPTY_CHANGES: RequestChanges = { arrived: [], moved: [], left: [] };

export const diffRequests = (
  previous: readonly VacationRequest[] | null,
  next: readonly VacationRequest[],
): RequestChanges => {
  /*
   * The first delivery is the state the screen already has, not news. Reporting
   * it would announce every open request as newly arrived on each page load —
   * five toasts for somebody who has simply come back to their board.
   */
  if (previous === null) return EMPTY_CHANGES;

  const before = new Map(previous.map((request) => [request.id, request]));
  const stillHere = new Set(next.map((request) => request.id));

  const arrived: RequestArrival[] = [];
  const moved: RequestTransition[] = [];
  const left = previous.filter((request) => !stillHere.has(request.id));

  for (const request of next) {
    const old = before.get(request.id);

    if (old === undefined) {
      arrived.push({ request });
      continue;
    }

    // Only the status. A request whose notes or PDF changed is not something to
    // interrupt anybody about, and announcing it would train people to dismiss
    // the ones that matter.
    if (old.status !== request.status) {
      moved.push({ request, from: old.status, to: request.status });
    }
  }

  return { arrived, moved, left };
};
